import { useEffect, useRef } from "react";
import * as d3 from "d3";

import type { EnrollmentTargetPoint } from "@/views/TrialEnrollment/enrollmentTarget.types";

const MARGIN = { top: 16, right: 32, bottom: 36, left: 58 };
const HEIGHT = 220;
const PLAN_COLOR = "#06709B";
const TARGET_COLOR = "#9CA3AF";

const parseMonth = d3.timeParse("%Y-%m");
const formatMonth = d3.timeFormat("%b %Y");

/** "2026-03" -> "Mar 2026"; falls back to the raw key if it doesn't parse. */
function formatMonthLabel(month: string): string {
  const parsed = parseMonth(month);
  return parsed ? formatMonth(parsed) : month;
}

/**
 * The sponsor's planned enrolment curve.
 *
 * Deliberately one series. Actuals and the vendor's curve are plotted against plan on the
 * Enrollment screen; this is the shape of the numbers in the table below it, so a target
 * that was typed wrong is visible as a kink rather than having to be read row by row.
 */
export function EnrollmentTargetCurve({
  data,
  totalTarget,
}: {
  data: EnrollmentTargetPoint[];
  /** The study target, derived from the numbers so editing the last month moves it. */
  totalTarget: number;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data.length || !svgRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth || 720;
    const width = containerWidth - MARGIN.left - MARGIN.right;
    const height = HEIGHT - MARGIN.top - MARGIN.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", containerWidth).attr("height", HEIGHT);

    const plot = svg
      .append("g")
      .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    const x = d3
      .scalePoint<string>()
      .domain(data.map((point) => point.month))
      .range([0, width]);

    const y = d3
      .scaleLinear()
      .domain([0, totalTarget * 1.05])
      .range([height, 0]);

    plot
      .append("g")
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickSize(-width)
          .tickFormat(() => ""),
      )
      .call((group) => group.select(".domain").remove())
      .selectAll("line")
      .attr("stroke", "#F0F2F4");

    plot
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(
            data
              .map((point) => point.month)
              .filter((_, index) => index % 2 === 0),
          )
          .tickFormat(formatMonthLabel),
      )
      .call((group) => group.select(".domain").attr("stroke", "#E5E7EB"))
      .selectAll("text")
      .attr("font-size", 11)
      .attr("fill", "#6B7280");

    plot
      .append("g")
      .call(d3.axisLeft(y).ticks(5))
      .call((group) => group.select(".domain").remove())
      .selectAll("text")
      .attr("font-size", 11)
      .attr("fill", "#6B7280");

    plot
      .append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", y(totalTarget))
      .attr("y2", y(totalTarget))
      .attr("stroke", TARGET_COLOR)
      .attr("stroke-dasharray", "4 4");

    plot
      .append("text")
      .attr("x", width)
      .attr("y", y(totalTarget) - 6)
      .attr("text-anchor", "end")
      .attr("font-size", 11)
      .attr("fill", TARGET_COLOR)
      .text(`Target ${totalTarget}`);

    const line = d3
      .line<EnrollmentTargetPoint>()
      .x((point) => x(point.month) ?? 0)
      .y((point) => y(point.sponsorPlan));

    plot
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", PLAN_COLOR)
      .attr("stroke-width", 2)
      .attr("d", line);

    plot
      .selectAll(null)
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (point) => x(point.month) ?? 0)
      .attr("cy", (point) => y(point.sponsorPlan))
      .attr("r", 2.5)
      .attr("fill", PLAN_COLOR);
  }, [data, totalTarget]);

  return (
    <div ref={containerRef} className="w-full">
      <svg ref={svgRef} />
    </div>
  );
}
